import { Loading, SplitPanel } from '@components/atomic/atoms';
import CheckList from '@components/checklist/checkList';
import { UserContext } from '@components/contexts/UserProvider';
import UploadDocument from '@components/form/upload/uploadDocuments';
import { IcoClose } from '@icons';
import { getModalData } from '@lib/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useContext, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { ResumeChecklistProjectType } from './ResumeChecklistCellRenderer';
import SummaryDownloadList from './SummaryDownloadList';
import { useParams } from 'react-router-dom';
import SummaryRecordModalInfo from './SummaryRecordModalInfo';
import { useHasPermission } from '@lib/hooks/useHasPermission';
import { useCanEditChecklist } from '@lib/hooks/useCanEditChecklist';
interface SummaryRecordModalProps {
	show: boolean;
	onHide: () => void;
	apiUrl: string;
	project: ResumeChecklistProjectType;
}

export type RecordDataType = {
	id: number;
	reported: boolean;
	reporting: boolean;
	checkliststatus?: {
		color: string;
	};
	project?: {
		cup?: string;
		startDate?: string;
	};
	projectExpenseItem?: {
		name?: string;
	};
	number?: string;
	serialNumber?: string;
	employee?: {
		surname?: string;
		name?: string;
	};
	operationalUnit?: string;
	company?: string;
	entryDate?: string;
	exitDate?: string;
	category?: string;
	sickDays?: string;
	vacationDays?: string;
	leaveDays?: string;
	rdiTypeCode?: string;
	details: {
		id: number;
		reported: boolean;
		year: number;
		month: number;
		day: number;
		workHours: number;
		projectHours: number;
		hourlyRate: number;
		projectCost: number;
		budgetResponsability: number;
		stream: {
			streamId: string;
			name: string;
		};
		wbe: string;
		cid: string;
	}[];
};

const SummaryRecordModal: React.FC<SummaryRecordModalProps> = ({ show, onHide: onHideFromParent, apiUrl, project }) => {
	const { userAccess } = useContext(UserContext);
	const { projectId } = useParams<{ projectId: string }>();

	const { data: modalData, isPending: isPendingModalData } = useQuery<{
		data: RecordDataType;
	}>({
		queryKey: ['modalData', apiUrl],
		queryFn: () => getModalData(apiUrl),
		enabled: show,
	});
	const recordData = modalData?.data;

	const queryClient = useQueryClient();
	const invalidateTimesheetsQuery = () => {
		queryClient.invalidateQueries({ queryKey: ['staff'] });
	};

	const [isEdited, setIsEdited] = useState(false);
	const onHide = () => {
		if (isEdited) {
			invalidateTimesheetsQuery();
		}
		setIsEdited(false);
		onHideFromParent();
	};

	const canEditChecklist = useCanEditChecklist('timesheets');
	const isDisabled = recordData?.reported || recordData?.reporting || !canEditChecklist;

	const shouldShowChecklist = useHasPermission('view-checklists');

	return (
		<Modal fullscreen show={show} onHide={onHide}>
			<Modal.Header
				style={{
					padding: '2rem',
					justifyContent: 'space-between',
				}}
			>
				<Modal.Title>
					<h2 style={{ fontSize: '28px', marginBottom: '0.5rem' }}>Controllo del record {recordData?.id}</h2>
					<h3
						style={{
							fontSize: '18px',
							fontWeight: 700,
						}}
					>
						PROGETTO: {project.name}
					</h3>
				</Modal.Title>
				<Button
					className="btn custom-ghost-button"
					onClick={onHide}
					style={{
						color: 'var(--np-primary-color)',
						display: 'flex',
						alignItems: 'center',
						gap: '0.75rem',
					}}
				>
					Chiudi
					<IcoClose
						style={{
							fill: 'var(--np-primary-color)',
						}}
					/>
				</Button>
			</Modal.Header>
			<Modal.Body
				style={{
					padding: '2rem',
					overflow: 'hidden',
				}}
			>
				{isPendingModalData ? (
					<Loading />
				) : show ? (
					<SplitPanel
						panels={[
							{
								key: 'info',
								title: 'Informazioni',
								content: <SummaryRecordModalInfo recordData={recordData!} project={project} />,
								...(shouldShowChecklist ? { defaultIsVisible: false } : {}),
							},
							{
								key: 'documents',
								title: 'Documenti',
								content: (
									<div>
										<UploadDocument
											entity="timesheets"
											entityId={recordData.id}
											projectId={projectId}
											disableEdit={!userAccess.isPartner}
										/>
									</div>
								),
							},
							...(shouldShowChecklist
								? [
										{
											key: 'checklist',
											title: 'Checklist',
											content: (
												<div className="custom-checklist-container">
													<div>
														<h4>Checklist per il controllo</h4>
														<SummaryDownloadList
															entity={{
																entityType: 'timesheets',
																entityId: recordData.id,
																label: 'timesheets',
															}}
														/>
													</div>
													{/* <h4>Checklist per il controllo</h4> */}
													{recordData.id && (
														<CheckList
															hasStandardChecklist={false}
															entityType="timesheets"
															entityId={recordData.id}
															disabled={isDisabled}
															hasInternalDownload={false}
														/>
													)}
												</div>
											),
										},
									]
								: []),
						]}
					/>
				) : null}
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onHide} className="btn custom-outline-button custom-medium-button">
					Annulla
				</Button>
				{shouldShowChecklist ? (
					<Button
						onClick={() => {
							setIsEdited(true);
						}}
						disabled={isDisabled}
						type="submit"
						form="checklist-timesheets"
						className="btn custom-primary-button custom-medium-button"
					>
						Salva risposte
					</Button>
				) : null}
			</Modal.Footer>
		</Modal>
	);
};

export default SummaryRecordModal;
